/**
 * ReviewReportScreen — revisión final del informe + borradores de email (Fase 57).
 *
 * Layout de dos paneles: izquierdo con el informe renderizado (markdown),
 * derecho con la lista de borradores de email. Cada draft se expande
 * inline para ver el cuerpo completo.
 *
 * Accesible desde MeetingsScreen cuando pipelineStatus ∈ {drafted, done}.
 * Coexiste con ReportScreen (que es solo lectura del informe sin drafts).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import styled from "styled-components";
import { marked } from "marked";

import { currentProcessingIdAtom } from "../../state/atoms.ts";
import { getProcessingFile, fetchTextFromPar, closePipeline, submitMetrics } from "../../platform/api.ts";
import type { EmailDraft, EmailDrafts } from "../../platform/api.ts";
import { Button } from "../components/Button.tsx";
import { DraftEditor } from "../components/DraftEditor.tsx";
import { generateEml, downloadBlob, downloadBlobAsZip } from "../../platform/browser-fs";
import { useStepTimer } from "../hooks/useStepTimer.ts";
import { useNavigateTo, useSyncParams } from "../routing.ts";

// ---------------------------------------------------------------------------
// Tipos de estado
// ---------------------------------------------------------------------------

type ReviewReportState = {
  report: { markdown: string } | { kind: "error"; message: string } | null;
  drafts: EmailDrafts | { kind: "error"; message: string } | null;
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function ReviewReportScreen() {
  const processingId = useAtomValue(currentProcessingIdAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  const [state, setState] = useState<ReviewReportState>({ report: null, drafts: null });
  const [expandedDrafts, setExpandedDrafts] = useState<Set<string>>(new Set());
  const [editingDrafts, setEditingDrafts] = useState<Set<string>>(new Set());
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closed, setClosed] = useState(false);

  // ── Métricas (Fase 61) ────────────────────────────────────────────────────
  const getElapsed = useStepTimer();
  const draftsDownloadedRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!processingId) return;

      setState({ report: null, drafts: null });
      setExpandedDrafts(new Set());

      try {
        const [reportRes, draftsRes] = await Promise.all([
          (async () => {
            const { downloadUrl } = await getProcessingFile(processingId, "report.md");
            return { markdown: await fetchTextFromPar(downloadUrl) };
          })(),
          (async () => {
            const { downloadUrl } = await getProcessingFile(processingId, "email-drafts.json");
            const text = await fetchTextFromPar(downloadUrl);
            return JSON.parse(text) as EmailDrafts;
          })(),
        ]);

        if (!cancelled) {
          setState({ report: reportRes, drafts: draftsRes });
        }
      } catch (err) {
        if (!cancelled) {
          // Intentar cargar individualmente para dar errores más específicos
          const newState: ReviewReportState = { report: null, drafts: null };

          try {
            const { downloadUrl } = await getProcessingFile(processingId, "report.md");
            newState.report = { markdown: await fetchTextFromPar(downloadUrl) };
          } catch (e) {
            newState.report = { kind: "error", message: e instanceof Error ? e.message : String(e) };
          }

          try {
            const { downloadUrl } = await getProcessingFile(processingId, "email-drafts.json");
            const text = await fetchTextFromPar(downloadUrl);
            newState.drafts = JSON.parse(text) as EmailDrafts;
          } catch (e) {
            newState.drafts = { kind: "error", message: e instanceof Error ? e.message : String(e) };
          }

          if (!cancelled) setState(newState);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [processingId]);

  const reportHtml = useMemo(() => {
    if (!state.report || "kind" in state.report) return "";
    try {
      return marked.parse(state.report.markdown, { async: false }) as string;
    } catch {
      return "<p>Error al renderizar el markdown.</p>";
    }
  }, [state.report]);

  const toggleDraft = (id: string) => {
    setExpandedDrafts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleEdit = (id: string) => {
    setEditingDrafts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Auto-expand when editing starts
    setExpandedDrafts((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleDraftUpdated = (updated: EmailDraft) => {
    if (!state.drafts || "kind" in state.drafts) return;
    setState({
      ...state,
      drafts: {
        ...state.drafts,
        drafts: state.drafts.drafts.map((d) =>
          d.id === updated.id ? updated : d,
        ),
      },
    });
  };

  const handleClose = async () => {
    if (!processingId) return;
    setClosing(true);
    setCloseError(null);

    // Enviar métricas de P6 (non-fatal)
    submitMetrics(processingId, {
      timeSpent: { p6: getElapsed() },
      counts: { draftsDownloaded: draftsDownloadedRef.current },
    }).catch(() => {/* non-fatal */});

    try {
      await closePipeline(processingId);
      setClosed(true);
    } catch (err) {
      setCloseError(err instanceof Error ? err.message : String(err));
    } finally {
      setClosing(false);
    }
  };

  const handleBack = () => navigateTo.meetings();

  const isLoading = !state.report || !state.drafts;

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (!processingId) {
    return (
      <Container>
        <ErrorText>No hay reunión seleccionada.</ErrorText>
        <Button $variant="ghost" onClick={handleBack}>← Volver</Button>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Button $variant="ghost" onClick={handleBack}>← Volver</Button>
        <HeaderTitle>Revisión final</HeaderTitle>
        <div />{/* spacer */}
      </Header>

      {isLoading && <LoadingText>Cargando…</LoadingText>}

      {!isLoading && (
        <Panels>
          {/* ── Panel izquierdo: Informe ── */}
          <LeftPanel>
            <PanelTitle>📄 Informe</PanelTitle>
            {"kind" in state.report! ? (
              <ErrorBox>
                <ErrorText>Error al cargar el informe: {state.report!.message}</ErrorText>
              </ErrorBox>
            ) : (
              <ReportBody dangerouslySetInnerHTML={{ __html: reportHtml }} />
            )}
          </LeftPanel>

          {/* ── Panel derecho: Drafts ── */}
          <RightPanel>
            <PanelTitle>
              📧 Borradores de email
              {"drafts" in state.drafts! && (
                <DraftCount>{state.drafts!.drafts.length}</DraftCount>
              )}
            </PanelTitle>
            {"kind" in state.drafts! ? (
              <ErrorBox>
                <ErrorText>Error al cargar los drafts: {state.drafts!.message}</ErrorText>
              </ErrorBox>
            ) : state.drafts!.drafts.length === 0 ? (
              <EmptyText>No hay borradores de email. No se detectaron tareas que requieran notificación.</EmptyText>
            ) : (
              <DraftList>
                {state.drafts!.drafts.map((draft) => {
                  const isExpanded = expandedDrafts.has(draft.id);
                  const isEditing = editingDrafts.has(draft.id);
                  return (
                    <DraftCard key={draft.id} $excluded={draft.excluded}>
                      <DraftHeader>
                        <DraftInfo>
                          <DraftToName>
                            {draft.toName}
                            {draft.excluded && <ExcludedTag>No enviar</ExcludedTag>}
                          </DraftToName>
                          {draft.to && <DraftEmail>{draft.to}</DraftEmail>}
                        </DraftInfo>
                        <DraftActions>
                          <Button
                            $variant="ghost"
                            onClick={() => toggleEdit(draft.id)}
                          >
                            {isEditing ? "✓ Listo" : "✏️ Editar"}
                          </Button>
                          <Button
                            $variant="ghost"
                            onClick={() => toggleDraft(draft.id)}
                          >
                            {isExpanded ? "▲ Ocultar" : "👁 Ver"}
                          </Button>
                          <Button
                            $variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              const eml = generateEml(draft);
                              const filename = `${draft.toName.replace(/\s+/g, "_")}-trama.eml`;
                              downloadBlob(filename, eml, "message/rfc822");
                              draftsDownloadedRef.current += 1;
                            }}
                          >
                            ↓ .eml
                          </Button>
                        </DraftActions>
                      </DraftHeader>
                      <DraftSubject>{draft.subject}</DraftSubject>
                      <DraftReason>{draft.reason}</DraftReason>
                      {isExpanded && !isEditing && (
                        <DraftBody>
                          <Divider />
                          <DraftBodyText>{draft.body}</DraftBodyText>
                        </DraftBody>
                      )}
                      {isEditing && (
                        <DraftEditor
                          processingId={processingId!}
                          draft={draft}
                          onDraftUpdated={handleDraftUpdated}
                        />
                      )}
                    </DraftCard>
                  );
                })}
              </DraftList>
            )}
            {"drafts" in state.drafts! && state.drafts!.drafts.length > 0 && (
              <DownloadAllRow>
                <Button
                  $variant="secondary"
                  onClick={() => {
                    const activeDrafts = "drafts" in state.drafts!
                      ? state.drafts!.drafts.filter((d) => !d.excluded)
                      : [];
                    const files = activeDrafts.map((d) => ({
                      filename: `${d.toName.replace(/\s+/g, "_")}-trama.eml`,
                      content: generateEml(d),
                    }));
                    void downloadBlobAsZip(
                      files,
                      `${processingId!}-drafts.zip`,
                    );
                    draftsDownloadedRef.current += files.length;
                  }}
                >
                  ↓ Descargar todos (.zip)
                </Button>
              </DownloadAllRow>
            )}
          </RightPanel>
        </Panels>
      )}

      {!isLoading && !("kind" in state.drafts!) && (
        <Footer>
          {closed ? (
            <ClosedBanner>
              ✅ Reunión cerrada — el informe y los drafts quedan disponibles para consulta.
            </ClosedBanner>
          ) : (
            <>
              {closeError && <CloseError>Error: {closeError}</CloseError>}
              <Button
                $variant="primary"
                onClick={handleClose}
                disabled={closing}
              >
                {closing ? "⏳ Cerrando…" : "✓ Cerrar reunión"}
              </Button>
            </>
          )}
        </Footer>
      )}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  text-align: center;
  padding: ${({ theme }) => theme.space.xxl} 0;
`;

const ErrorBox = styled.div`
  padding: ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.color.errorMuted};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.color.error};
  margin: 0;
`;

const EmptyText = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  margin: 0;
`;

const Panels = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.lg};
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const PanelTitle = styled.h2`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0 0 ${({ theme }) => theme.space.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const DraftCount = styled.span`
  background: ${({ theme }) => theme.color.accentMuted};
  color: ${({ theme }) => theme.color.accent};
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
`;

const ReportBody = styled.div`
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.font.size.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textPrimary};
  max-height: calc(100vh - 160px);
  overflow-y: auto;

  h1 { font-size: ${({ theme }) => theme.font.size.xl}; margin: 0 0 ${({ theme }) => theme.space.md} 0; }
  h2 { font-size: ${({ theme }) => theme.font.size.lg}; margin: ${({ theme }) => theme.space.lg} 0 ${({ theme }) => theme.space.sm} 0; }
  h3 { font-size: ${({ theme }) => theme.font.size.md}; font-weight: ${({ theme }) => theme.font.weight.bold}; margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.xs} 0; }
  h4 { font-size: ${({ theme }) => theme.font.size.sm}; font-weight: ${({ theme }) => theme.font.weight.bold}; margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.xs} 0; }
  h5 { font-size: ${({ theme }) => theme.font.size.sm}; font-weight: ${({ theme }) => theme.font.weight.medium}; margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.xs} 0; color: ${({ theme }) => theme.color.textMuted}; }
  p { margin: 0 0 ${({ theme }) => theme.space.sm} 0; }
  ul, ol { margin: 0 0 ${({ theme }) => theme.space.sm} 0; padding-left: ${({ theme }) => theme.space.lg}; }
  li { margin-bottom: ${({ theme }) => theme.space.xs}; }
  table { width: 100%; border-collapse: collapse; margin: ${({ theme }) => theme.space.sm} 0 ${({ theme }) => theme.space.md} 0; font-size: ${({ theme }) => theme.font.size.xs}; }
  th { background: ${({ theme }) => theme.color.accentMuted}; font-weight: ${({ theme }) => theme.font.weight.bold}; text-align: left; padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm}; border: 1px solid ${({ theme }) => theme.color.border}; }
  td { padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm}; border: 1px solid ${({ theme }) => theme.color.border}; vertical-align: top; }
  tr:nth-child(even) td { background: ${({ theme }) => theme.color.background}; }
  code { font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.font.size.xs}; background: ${({ theme }) => theme.color.background}; padding: 0.125rem 0.25rem; border-radius: ${({ theme }) => theme.radius.sm}; }
  pre { background: ${({ theme }) => theme.color.background}; border: 1px solid ${({ theme }) => theme.color.border}; border-radius: ${({ theme }) => theme.radius.md}; padding: ${({ theme }) => theme.space.sm}; overflow-x: auto; margin: ${({ theme }) => theme.space.sm} 0; }
  pre code { background: none; border: none; padding: 0; }
  blockquote { border-left: 3px solid ${({ theme }) => theme.color.accentMuted}; margin: ${({ theme }) => theme.space.sm} 0; padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.md}; color: ${({ theme }) => theme.color.textMuted}; }
  hr { border: none; border-top: 1px solid ${({ theme }) => theme.color.border}; margin: ${({ theme }) => theme.space.lg} 0; }
`;

const DraftList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const DraftCard = styled.div<{ $excluded?: boolean }>`
  background: ${({ theme, $excluded }) =>
    $excluded ? theme.color.background : theme.color.surface};
  border: 1px solid ${({ theme, $excluded }) =>
    $excluded ? theme.color.borderStrong : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.md};
  opacity: ${({ $excluded }) => ($excluded ? 0.7 : 1)};
`;

const DraftHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
`;

const DraftInfo = styled.div`
  min-width: 0;
`;

const DraftToName = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.bold};
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const DraftEmail = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-top: 0.125rem;
`;

const DraftActions = styled.div`
  flex-shrink: 0;
`;

const DraftSubject = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.accent};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  margin-top: ${({ theme }) => theme.space.xs};
`;

const DraftReason = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textDim};
  margin-top: ${({ theme }) => theme.space.xs};
  font-style: italic;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.border};
  margin: ${({ theme }) => theme.space.sm} 0;
`;

const DraftBody = styled.div`
  margin-top: ${({ theme }) => theme.space.xs};
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;

const ClosedBanner = styled.div`
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.success};
  background: ${({ theme }) => theme.color.successMuted};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  border-radius: ${({ theme }) => theme.radius.md};
  text-align: center;
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const CloseError = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.error};
  background: ${({ theme }) => theme.color.errorMuted};
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  border-radius: ${({ theme }) => theme.radius.sm};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const DownloadAllRow = styled.div`
  margin-top: ${({ theme }) => theme.space.md};
  padding-top: ${({ theme }) => theme.space.sm};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;

const ExcludedTag = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.warning};
  background: ${({ theme }) => theme.color.warningMuted};
  padding: 0.125rem 0.375rem;
  border-radius: 999px;
  margin-left: ${({ theme }) => theme.space.sm};
  vertical-align: middle;
`;

const DraftBodyText = styled.pre`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: pre-wrap;
  line-height: 1.6;
  margin: 0;
`;
